"use client";
import {
  ButtonComponent,
  CheckboxComponent,
  LoaderComponent,
  PaperComponent,
} from "@/components";
import {
  GET_PRODUCT_APPLICATION,
  IMAGES,
  PRODUCT_HANDLER,
  WEB_COLORS,
  WEB_EVENTS,
} from "@/lib/constants";
import useErrorHandling from "@/lib/hooks/errorhandling";
import { sendEventAnalytics } from "@/lib/hooks/eventAnalytics";
import {
  useAddAddressMutation,
  useConfirmAddressMutation,
  useGetAddressQuery,
  useGetCustomerQuery,
  usePatchAddressMutation,
  usePatchApplicationMutation,
  usePatchRplAddressMutation,
} from "@/lib/redux";
import { Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useFormik } from "formik";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useCallback, useMemo, useState } from "react";
import { initialAddressState } from "@/components/Address";
import { PersonalAddressDrawer } from "./PersonalAddressDrawer";
import { checkForRpl } from "@/lib/utils/rpl.utils";

function PersonalAddressComponent({
  product,
  applicationData,
  applicationFetchError,
}) {
  //HOOKS
  const t = useTranslations("PersonalDetails.address");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [openMapDrawer, setOpenMapDrawer] = useState(false);

  const { data: customerData, isLoading: fetchingCustomer } =
    useGetCustomerQuery();
  const [
    patchApplicationAddress,
    { isFetching: isPatchingAddress, error: patchAddressError },
  ] = usePatchApplicationMutation();

  const [
    postAddress,
    {
      isSuccess: addressAdded,
      isLoading: addingAddress,
      error: postAddressError,
    },
  ] = useAddAddressMutation();
  const [
    updateAddress,
    {
      data: updateMapData,
      isLoading: updatingAddress,
      isSuccess: addressUpdated,
      error: updateAddressError,
    },
  ] = usePatchAddressMutation();
  const [
    updateRplAddress,
    {
      data: updateRplAddressData,
      isLoading: updatingRplAddress,
      isSuccess: isSuccessRplAddressUpdated,
      error: updateRplAddressError,
    },
  ] = usePatchRplAddressMutation();

  //API ENTITIES
  const productApplication = GET_PRODUCT_APPLICATION(
    PRODUCT_HANDLER[product],
    applicationData?.data,
  );

  const isRPL = checkForRpl(productApplication);

  const appId = useMemo(() => productApplication?.appId, [productApplication]);

  const {
    data: addressData,
    isFetching: isFetchingAddress,
    error: fetchAddressError,
  } = useGetAddressQuery(productApplication?.appId, {
    skip: !productApplication?.appId,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  const [confirmAddress, { isLoading: isConfirmingAddress }] =
    useConfirmAddressMutation();

  // address already stored in db -> edit flow
  const plSelectedAddress = addressData?.data?.addresses?.find(
    (address) => address?.addressId === productApplication?.addressId,
  );
  const isError =
    patchAddressError ||
    fetchAddressError ||
    updateAddressError ||
    postAddressError || updateRplAddressError;

  const addressDataArray = addressData?.data?.addresses;
  const mailingAddress =
    addressDataArray?.find((address) => address?.addressType === "MAILING") ||
    "";

  const userProvidedAddressId = useMemo(
    () =>
      addressDataArray?.find((item) => item?.source === "USER_PROVIDED")
        ?.addressId,
    [addressDataArray],
  );

  useErrorHandling(isError);

  const patchAddressInApplication = useCallback(
    async (addressId) => {
      sendEventAnalytics({
        eventName: isRPL ? WEB_EVENTS.RL_ALTERNATE_ADDRESS_CHANGED : WEB_EVENTS.MAILING_ADDRESS_STARTED,
        ...productApplication,
        ...customerData?.data,
      });
      await patchApplicationAddress({
        addressId,
        appId,
        addressVerifiedType: addressDataArray?.find(
          (address) => address?.addressId === addressId,
        )?.verifiedType,
      });
    },
    [
      sendEventAnalytics,
      productApplication,
      isRPL,
      appId,
      addressDataArray,
      customerData,
    ],
  );

  const handleAddressSubmit = useCallback(
    async (addressData, id, addressId) => {
      try {
        if (isRPL) {
          await updateRplAddress({ ...addressData, addressId, appId: id }).unwrap();
          sendEventAnalytics({
            eventName: WEB_EVENTS.RL_ALTERNATE_ADDRESS_CHANGED,
            ...productApplication,
            ...customerData?.data,
          });
        } else {
          if (addressId) {
            await updateAddress({
              ...addressData,
              addressId,
              appId: id,
            }).unwrap();
            patchAddressInApplication(addressId);
          } else {
            await postAddress({ ...addressData, appId: id }).unwrap();
          }
        }
        setOpenMapDrawer(false);
      } catch (error) {
        console.log("error in address submit", error);
      }
    },
    [
      updateAddress,
      postAddress,
      initialAddressState,
      setOpenMapDrawer,
      patchAddressInApplication,
      customerData?.data,
      productApplication,
      isRPL
    ]
  );

  const handleOnSubmitAddress = useCallback(
    (addressData) => {
      const newAddressData = {
        line1: addressData.line2,
        line2: addressData.line3,
        line3: addressData.line4,
        line4: addressData.line5,
        landmark: addressData.landmark,
        city: addressData.city,
        country: addressData.country,
        state: addressData.state,
        pin: addressData.pin,
        longitude: addressData.longitude,
        latitude: addressData.latitude,
      };
      if (typeof addressData?.isMapMyIndiaResult === "boolean") {
        newAddressData.isMapMyIndiaResult = Boolean(
          addressData?.isMapMyIndiaResult,
        );
      }
      let addressId = null;
      if(isRPL){
        addressId = mailingAddress?.addressId;
      }else {
        addressId = userProvidedAddressId;
      }
      handleAddressSubmit(newAddressData, appId, addressId);
    },
    [setOpenMapDrawer, appId, userProvidedAddressId,isRPL, mailingAddress?.addressId, handleAddressSubmit]
  );

  return (
    <>
      <LoaderComponent
        open={isPatchingAddress || isFetchingAddress || fetchingCustomer || updatingRplAddress}
      />
      {!isRPL && addressData?.data?.addresses
        .filter((address) => address?.source !== "USER_PROVIDED")
        ?.map((address) => (
          <PaperComponent
            elevation={0}
            paperStyle={{ px: 2, border: "1px solid #0041354D", m: 0 }}
            key={address?.addressId}
          >
            <Stack spacing={2}>
              <Stack spacing={1}>
                <Typography
                  textTransform={"capitalize"}
                  fontWeight={{ xs: 500, md: 600 }}
                  fontSize={{ xs: 12, sm: 14, md: 16 }}
                >
                  My {address?.addressType?.toLowerCase()} Address{" "}
                  <span
                    style={{ color: WEB_COLORS.fadeColor, fontWeight: 300 }}
                  >
                    {address?.source === "CKYC" ? "(as per CKYC)" : ""}
                  </span>
                </Typography>
                {address?.source === "CKYC" && (
                  <Typography fontSize={12} color={WEB_COLORS.fadeColor}>
                    We have fetched your{" "}
                    <span
                      style={{
                        textTransform: "capitalize",
                      }}
                    >
                      {address?.addressType?.toLowerCase()} address
                    </span>{" "}
                    based on the KYC details
                  </Typography>
                )}
              </Stack>
              <Typography
                fontWeight={{ xs: 500 }}
                fontSize={{ xs: 12, sm: 14, md: 16 }}
                textTransform={"capitalize"}
              >
                {[
                  address?.line1,
                  address?.line2,
                  address?.city,
                  address?.state,
                  `(${address?.pin})`,
                ]
                  .filter(Boolean)
                  ?.join(", ")
                  ?.toLowerCase()}
              </Typography>
              {addressData?.data?.addresses.filter(
                (address) => address.source === "CKYC",
              ).length < 2 &&
                address?.addressType?.toLowerCase() === "permanent" && (
                  <CheckboxComponent
                    onChange={async (e) => {
                      await confirmAddress({
                        addressId: !productApplication?.addressId
                          ? address?.addressId
                          : null,
                        consent: "y",
                        appId: productApplication?.appId,
                      });
                    }}
                    checked={!!productApplication?.addressId}
                    color={WEB_COLORS.errorColor}
                    label={
                      <Typography
                        color={WEB_COLORS.textFadeColor}
                        fontWeight={400}
                        fontSize={{ md: "14px", xs: "10px" }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "fit-content",
                        }}
                      >
                        {"This is also my current address."}
                      </Typography>
                    }
                  />
                )}
            </Stack>
          </PaperComponent>
        ))}

      <PaperComponent
        elevation={0}
        paperStyle={{
          px: 2,
          position: "relative",
          border: "1px solid #0041354D",
        }}
      >
        <Stack spacing={2}>
          <Typography fontSize={{ xs: 12, sm: 14, md: 16 }}>
            {isRPL ? t("currentmailingaddress") : t("mailingaddress")}
          </Typography>
          <Typography
            color={WEB_COLORS.fadeColor}
            fontSize={{ xs: 10, md: 12 }}
          >
            ⚡️ Join 92% of customers who got faster loan approvals by providing
            this address
          </Typography>
          {mailingAddress && (
            <Typography
              fontWeight={500}
              fontSize={{ xs: 12, sm: 14, md: 16 }}
              textTransform={"capitalize"}
            >
              {[
                mailingAddress?.line1,
                mailingAddress?.line2,
                mailingAddress?.city,
                mailingAddress?.state,
                mailingAddress?.pin,
              ]
                .filter(Boolean)
                .join(", ")
                .toLowerCase()}
            </Typography>
          )}
        </Stack>
        <Stack spacing={2} mt={2}>
          {!isRPL && ((!productApplication?.addressId &&
            addressData?.data?.addresses.filter(
              (address) => address.source === "CKYC",
            ).length < 2) ||
          (productApplication?.addressId &&
            addressData?.data?.addresses.filter(
              (address) => address.source === "CKYC",
            ).length === 0) ? (
            <Image
              src={IMAGES.mandatory}
              alt="Mandatory"
              style={{
                position: "absolute",
                top: 15,
                right: isMobile ? -4 : -6,
                width: isMobile ? 50 : 80,
                height: "auto",
              }}
            />
          ) : (
            <Image
              src={IMAGES.recommended}
              alt="Recommended"
              style={{
                position: "absolute",
                top: 15,
                right: isMobile ? -4 : -6,
                width: isMobile ? 50 : 100,
                height: "auto",
              }}
            />
          ))}
          <Stack
            direction="row"
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent={"flex-start"}
            spacing={1}
          >
            <Image src={IMAGES.infoGreen} alt="info-icon" />
            <Typography
              color={"#407168"}
              fontWeight={400}
              fontSize={{ xs: 14, sm: 16 }}
            >
              Incorrect information will lead to immediate rejection
            </Typography>
          </Stack>
          <Stack
            spacing={1}
            direction="row"
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent={"flex-start"}
          >
            <Image src={IMAGES.infoGreen} alt="info-icon" />
            <Typography
              color={"#407168"}
              fontWeight={400}
              fontSize={{ xs: 14, sm: 16 }}
            >
              Physical verification over this address may be conducted
            </Typography>
          </Stack>
          <ButtonComponent
            variant="outlined"
            type="reset"
            buttonStyle={{ width: "fit-content", py: 1 }}
            onClick={() => setOpenMapDrawer(true)}
          >
            {mailingAddress ? ( isRPL ? "Change My Address" :"Edit Address" ): "+ Add Address"}
          </ButtonComponent>
        </Stack>
      </PaperComponent>

      <PersonalAddressDrawer
        isLoading={addingAddress || updatingAddress}
        handleDrawer={setOpenMapDrawer}
        open={openMapDrawer}
        handleOnSubmit={handleOnSubmitAddress}
      />
    </>
  );
}

export default PersonalAddressComponent;
